import { fak as fakBloom } from '@awesome.me/kit-2c0e92f331/icons';
import type { IconDefinition, SizeProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box } from '@mui/material';
import { ImageSize, getImageSize } from '@sage/ui-components';

import AccessibilityDeviceIcon from 'src/assets/images/device-icons/accessibility-device-icon.svg?react';
import CheckInButtonIcon from 'src/assets/images/device-icons/check-in-button-icon.svg?react';
import CheckInSensorIcon from 'src/assets/images/device-icons/check-in-sensor.svg?react';
import DoorAlarmIcon from 'src/assets/images/device-icons/door-alarm-icon.svg?react';
import FallDetectionDeviceIcon from 'src/assets/images/device-icons/fall-detection-device-icon.svg?react';
import PendantButtonIcon from 'src/assets/images/device-icons/pendant-button-icon.svg?react';
import UnassignedButtonIcon from 'src/assets/images/device-icons/unassigned-button-icon.svg?react';
import type { DeviceIconType } from 'src/hooks/useDeviceAllocationProperties';

export type DeviceIconSize = Extract<ImageSize, 'small' | 'medium'>;

const faSizeFor: Record<DeviceIconSize, SizeProp> = {
    small: 'lg',
    medium: '2x',
};

const CircledKitIcon = ({
    icon,
    px,
    faSize,
}: {
    icon: IconDefinition;
    px: string;
    faSize: SizeProp;
}) => (
    <Box
        sx={{
            width: px,
            height: px,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            bgcolor: 'purple.100',
            border: '2px solid',
            borderColor: 'common.white',
            color: 'grey.950',
        }}
    >
        <FontAwesomeIcon icon={icon} size={faSize} />
    </Box>
);

const buildIcons = (size: DeviceIconSize): Record<DeviceIconType, React.ReactNode> => {
    const px = getImageSize(size);
    const faSize = faSizeFor[size];

    return {
        'accessibility-device': <AccessibilityDeviceIcon width={px} height={px} />,
        'check-in': <CheckInButtonIcon width={px} height={px} />,
        'check-in-sensor': <CheckInSensorIcon width={px} height={px} />,
        pendant: <PendantButtonIcon width={px} height={px} />,
        'button-with-reset': (
            <CircledKitIcon
                icon={fakBloom.faButtonInovonics as IconDefinition}
                px={px}
                faSize={faSize}
            />
        ),
        'pull-cord': (
            <CircledKitIcon icon={fakBloom.faPullCord as IconDefinition} px={px} faSize={faSize} />
        ),
        'door-alarm': <DoorAlarmIcon width={px} height={px} />,
        'fall-detection': <FallDetectionDeviceIcon width={px} height={px} />,
        unassigned: <UnassignedButtonIcon width={px} height={px} />,
    };
};

const iconsBySize: Record<DeviceIconSize, Record<DeviceIconType, React.ReactNode>> = {
    small: buildIcons('small'),
    medium: buildIcons('medium'),
};

interface DeviceIconProps {
    iconType: DeviceIconType;
    size?: DeviceIconSize;
}

export default function DeviceIcon({ iconType, size = 'medium' }: DeviceIconProps) {
    const icons = iconsBySize[size];
    return icons[iconType] ?? icons.unassigned;
}
